import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Form, Button, Spinner, Badge, Table, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import { leaveSourceLabel } from '../utils/leaveSource';
import { leaveSaveAlert } from '../utils/leaveSaveResult';
import { leaveRangeShort } from '../utils/leaveRange';

const API = process.env.REACT_APP_API;

const LEAVE_TYPES = [
  { value: 'sick',     label: 'ลาป่วย', bg: 'warning' },
  { value: 'personal', label: 'ลากิจ',  bg: 'info' },
];
const typeInfo = (t) => LEAVE_TYPES.find(x => x.value === t) || { label: t, bg: 'secondary' };

const todayStr = () => new Date().toISOString().split('T')[0];
const pad2 = (n) => String(n).padStart(2, '0');
const addDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
// จำนวนวันลาแบบนับรวมวันเริ่มและวันสิ้นสุด (0 = ช่วงวันที่ไม่ถูกต้อง)
const dayCount = (start, end) => {
  if (!start || !end || start > end) return 0;
  const [y1, m1, d1] = start.split('-').map(Number);
  const [y2, m2, d2] = end.split('-').map(Number);
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000) + 1;
};
const DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd;

export default function StudentLeave() {
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const authHeader = { headers: { authorization: `Bearer ${getToken()}` } };
  const advisorId = getUserId();

  const [yearId, setYearId] = useState(null);
  const [currentYearLabel, setCurrentYearLabel] = useState("");

  const [students, setStudents] = useState([]);
  const [leaves, setLeaves] = useState([]);

  // ฟอร์ม
  const [selectedIds, setSelectedIds] = useState([]);   // study_id ที่เลือก
  const [leaveStart, setLeaveStart] = useState(todayStr());
  const [leaveEnd, setLeaveEnd] = useState(todayStr());
  const [leaveType, setLeaveType] = useState('sick');
  const [leaveNote, setLeaveNote] = useState('');
  const [saving, setSaving] = useState(false);

  // ─── โหลดปีการศึกษาปัจจุบัน (ล่าสุด) ───────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${API}/getStudyYear`, authHeader)
      .then((res) => {
        const current = (res.data || [])[0];   // เรียง year_id DESC → ตัวแรกคือปีปัจจุบัน
        if (current) {
          setYearId(current.year_id);
          setCurrentYearLabel(`ปีการศึกษา ${current.year_year}`);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── โหลดนักเรียน + รายการลา ────────────────────────────────────────────────
  const loadLeaves = useCallback(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getStudentLeaves/${advisorId}/${yearId}`, authHeader)
      .then((res) => setLeaves(res.data || []))
      .catch(() => setLeaves([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, advisorId]);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${API}/getStudentsByAdvisorRoom/${advisorId}/${yearId}`, authHeader)
      .then((res) => setStudents((res.data || []).filter(s => String(s.st_status) !== '0')))
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
    loadLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId]);

  // ─── คนที่มีการแจ้งลาคาบเกี่ยวกับช่วงที่กำลังกรอกอยู่แล้ว ────────────────────
  // leaves มาจากทุกฝ่ายที่แจ้งได้ (ครูประจำชั้น/ฝ่ายปกครอง/หัวหน้าวิชาการ/ครูประจำชั้น
  // ศาสนา) ไม่ใช่เฉพาะที่ครูคนนี้แจ้ง จึงกันการแจ้งซ้ำข้ามฝ่ายได้จริง
  const leavesByStudy = useMemo(() => {
    const map = new Map();
    leaves.forEach(l => {
      const key = String(l.study_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    return map;
  }, [leaves]);

  const existingFor = useCallback((studyId) => (
    (leavesByStudy.get(String(studyId)) || [])
      .filter(l => overlaps(leaveStart, leaveEnd, l.leave_start, l.leave_end))
  ), [leavesByStudy, leaveStart, leaveEnd]);

  // ─── ฟอร์ม ─────────────────────────────────────────────────────────────────
  const toggleStudent = (studyId) => {
    setSelectedIds(prev => prev.includes(studyId)
      ? prev.filter(id => id !== studyId)
      : [...prev, studyId]);
  };
  // "เลือกทั้งหมด" = ทุกคนที่ยังไม่มีใครแจ้งลาช่วงนี้ — เลือกคนที่ลาอยู่แล้วมาด้วย
  // ก็ถูก server ข้ามอยู่ดี แต่ครูจะนึกว่าบันทึกครบ
  const toggleAll = () => {
    const selectable = students.filter(s => existingFor(s.study_id).length === 0).map(s => s.study_id);
    setSelectedIds(prev => selectable.every(id => prev.includes(id)) ? [] : selectable);
  };

  const handleStartChange = (val) => {
    const days = dayCount(leaveStart, leaveEnd);
    setLeaveStart(val);
    setLeaveEnd(days > 0 ? addDays(val, days - 1) : val);   // คงจำนวนวันลาเดิมไว้
  };

  // เลือกจำนวนวัน → คำนวณวันที่สิ้นสุดให้จากวันที่เริ่ม
  const handleDaysChange = (val) => {
    if (!val) return;                                       // "กำหนดเอง" = ไม่แตะวันที่
    setLeaveEnd(addDays(leaveStart, Number(val) - 1));
  };

  const resetForm = () => {
    setSelectedIds([]);
    setLeaveStart(todayStr());
    setLeaveEnd(todayStr());
    setLeaveType('sick');
    setLeaveNote('');
  };

  const handleSave = () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกนักเรียน', text: 'กรุณาเลือกนักเรียนอย่างน้อย 1 คน' });
      return;
    }
    if (leaveStart > leaveEnd) {
      Swal.fire({ icon: 'warning', title: 'ช่วงวันที่ไม่ถูกต้อง', text: 'วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด' });
      return;
    }
    setSaving(true);
    axiosInstance.post(`${API}/saveStudentLeave`, {
      study_ids: selectedIds,
      leave_start: leaveStart,
      leave_end: leaveEnd,
      leave_type: leaveType,
      leave_note: leaveNote.trim() || null,
    }, authHeader)
      .then((res) => {
        Swal.fire(leaveSaveAlert(res.data));
        resetForm();
        loadLeaves();
      })
      .catch((err) => {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถบันทึกได้' });
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (leave) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: `ลบการลาของ ${leave.st_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    }).then((result) => {
      if (!result.isConfirmed) return;
      axiosInstance.delete(`${API}/deleteStudentLeave/${leave.leave_id}`, authHeader)
        .then(() => {
          Swal.fire({ icon: 'success', title: 'ลบสำเร็จ', timer: 1000, showConfirmButton: false });
          loadLeaves();
        })
        .catch((err) => {
          Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถลบได้' });
        });
    });
  };

  const today = todayStr();
  const leaveDays = dayCount(leaveStart, leaveEnd);
  const coversToday = (l) => l.leave_start <= today && today <= l.leave_end;
  const roomLabel = students.length > 0 ? `ม.${students[0].tb_class_c_id}/${students[0].r_no}` : '';
  const selectableCount = students.filter(s => existingFor(s.study_id).length === 0).length;
  const overlapSelected = selectedIds.filter(id => existingFor(id).length > 0).length;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item active">แจ้งลานักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">แจ้งลานักเรียน
              {roomLabel && <span className="fs-sm text-muted ms-2">ห้อง {roomLabel}</span>}
            </h4>
          </div>
          {currentYearLabel && (
            <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
              <Badge bg="primary" className="fs-sm">{currentYearLabel}</Badge>
            </div>
          )}
        </div>

        <Alert variant="info" className="d-flex align-items-center gap-2 mb-4">
          <i className="ri-information-line fs-5"></i>
          <span>รายการลาจะไปแสดงเป็นสถานะ "ลา" อัตโนมัติในหน้าเช็คชื่อรายคาบและคาบแนะแนวของวันที่อยู่ในช่วงลา
            &nbsp;— มีผลกับการเช็คชื่อ<strong>ครั้งถัดไป</strong>เท่านั้น หากคาบนั้นเช็คชื่อไปแล้วให้แก้ที่หน้าแก้ไขการเช็คชื่อ</span>
        </Alert>

        <Row className="g-3">
          {/* ── ฟอร์มแจ้งลา ── */}
          <Col xl="5">
            <Card className="card-one h-100">
              <Card.Header><Card.Title as="h6">เพิ่มการลา</Card.Title></Card.Header>
              <Card.Body className="p-3">
                <Row className="g-2 mb-3">
                  <Col xs="12">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">จำนวนวันลา</Form.Label>
                      <Form.Select
                        value={DAY_OPTIONS.includes(leaveDays) ? String(leaveDays) : ''}
                        onChange={e => handleDaysChange(e.target.value)}
                      >
                        <option value="">กำหนดเอง</option>
                        {DAY_OPTIONS.map(d => <option key={d} value={d}>{d} วัน</option>)}
                      </Form.Select>
                      <Form.Text className="text-muted">เลือกจำนวนวันแล้วระบบจะตั้งวันที่สิ้นสุดให้อัตโนมัติ</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">วันที่เริ่ม</Form.Label>
                      <Form.Control type="date" value={leaveStart} onChange={e => handleStartChange(e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">วันที่สิ้นสุด</Form.Label>
                      <Form.Control type="date" value={leaveEnd} min={leaveStart} onChange={e => setLeaveEnd(e.target.value)} />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">ประเภทการลา</Form.Label>
                  <div className="d-flex gap-3">
                    {LEAVE_TYPES.map(t => (
                      <Form.Check
                        key={t.value}
                        type="radio"
                        name="leaveType"
                        id={`leaveType-${t.value}`}
                        label={t.label}
                        checked={leaveType === t.value}
                        onChange={() => setLeaveType(t.value)}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">หมายเหตุ</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="เหตุผลการลา (ถ้ามี)"
                    value={leaveNote}
                    onChange={e => setLeaveNote(e.target.value)}
                    maxLength={255}
                  />
                </Form.Group>

                <Form.Group>
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label className="fw-semibold mb-0">เลือกนักเรียน ({selectedIds.length})</Form.Label>
                    {students.length > 0 && (
                      <Button variant="link" size="sm" className="p-0" onClick={toggleAll}>
                        {selectedIds.length > 0 && selectedIds.length === selectableCount ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                      </Button>
                    )}
                  </div>
                  <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6 }} className="p-2">
                    {students.length > 0 ? students.map(s => {
                      const already = existingFor(s.study_id);
                      return (
                        <div key={s.study_id} className="d-flex align-items-center gap-2 flex-wrap">
                          <Form.Check
                            type="checkbox"
                            id={`st-${s.study_id}`}
                            className="flex-grow-1"
                            label={`${s.st_idnumber} — ${s.st_name}`}
                            checked={selectedIds.includes(s.study_id)}
                            onChange={() => toggleStudent(s.study_id)}
                          />
                          {/* บอกช่วงวันลาที่มีอยู่แล้วให้เห็นบนหน้าจอ ไม่ใช่แค่ใน tooltip —
                              บนมือถือไม่มี hover และครูต้องรู้ว่าซ้ำช่วงไหนถึงจะตัดสินใจได้ */}
                          {already.map(l => (
                            <Badge key={l.leave_id} bg="info" className="fw-normal"
                              title={`${l.leave_start} ถึง ${l.leave_end}`}>
                              {leaveSourceLabel(l.source)}ลาแล้ว {leaveRangeShort(l.leave_start, l.leave_end)}
                            </Badge>
                          ))}
                        </div>
                      );
                    }) : <div className="text-muted text-center py-2">ไม่พบนักเรียน</div>}
                  </div>
                  {overlapSelected > 0 && (
                    <Alert variant="warning" className="mt-2 mb-0 py-2 px-3 fs-sm">
                      <i className="ri-alert-line me-1"></i>
                      เลือกไว้ {overlapSelected} คน ที่มีการแจ้งลาช่วงวันนี้อยู่แล้ว — ระบบจะข้ามให้ ไม่บันทึกซ้ำ
                    </Alert>
                  )}
                </Form.Group>

                <div className="mt-3 d-flex justify-content-end">
                  <Button variant="success" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><Spinner animation="border" size="sm" className="me-1" />กำลังบันทึก...</>
                      : <><i className="ri-save-line me-1"></i>บันทึกการลา</>}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* ── ตารางรายการลา ── */}
          <Col xl="7">
            <Card className="card-one h-100">
              <Card.Header><Card.Title as="h6">รายการลาทั้งหมด</Card.Title></Card.Header>
              <Card.Body className="p-3">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th scope="col">ชื่อ-สกุล</th>
                      <th scope="col" className="text-center" style={{ width: 70 }}>ห้อง</th>
                      <th scope="col" className="text-center" style={{ width: 170 }}>ช่วงวันลา</th>
                      <th scope="col" className="text-center" style={{ width: 80 }}>ประเภท</th>
                      <th scope="col" style={{ width: 150 }}>ผู้แจ้ง</th>
                      <th scope="col">หมายเหตุ</th>
                      <th scope="col" className="text-center" style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.length > 0 ? leaves.map(l => (
                      <tr key={l.leave_id} className={coversToday(l) ? 'table-info' : ''}>
                        <td>{l.st_name}</td>
                        <td className="text-center">{l.tb_class_c_id}/{l.r_no}</td>
                        <td className="text-center">
                          {l.leave_start === l.leave_end
                            ? l.leave_start
                            : <>{l.leave_start}<br />ถึง {l.leave_end}</>}
                        </td>
                        <td className="text-center">
                          <Badge bg={typeInfo(l.leave_type).bg}>{typeInfo(l.leave_type).label}</Badge>
                        </td>
                        <td><small>{leaveSourceLabel(l.source)}</small></td>
                        <td><small>{l.leave_note || '-'}</small></td>
                        <td className="text-center">
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(l)} title="ลบ">
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={7} className="text-center text-muted py-3">ยังไม่มีรายการลา</td></tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
